import { z } from "zod";
import { bookCreationProfileForTemplate, creationProductionFamilySchema, creationReviewGateIdSchema, creationReviewerSchema, } from "./creation-profile.js";
import { bookTemplates } from "./catalog.js";
import { opaqueIdSchema } from "./opaque.js";
export const pageUnitContractVersionSchema = z.literal(1);
export const pageUnitBookFamilySchema = creationProductionFamilySchema;
export const pageUnitTypeSchema = z.enum([
    "FRONT_MATTER",
    "NARRATIVE_CHAPTER",
    "PICTURE_BOOK_SPREAD",
    "RECIPE_PAGE",
    "COMIC_PAGE",
]);
const semanticContentIdSchema = z.string().trim().min(1).max(160);
const currentCatalogTemplateIdSchema = z.enum(bookTemplates.map(({ id }) => id));
const normalizedFrameSchema = z
    .object({
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
    width: z.number().positive().max(1),
    height: z.number().positive().max(1),
})
    .strict()
    .superRefine((frame, context) => {
    if (frame.x + frame.width > 1)
        context.addIssue({
            code: "custom",
            path: ["width"],
            message: "The frame must remain inside the normalized page width.",
        });
    if (frame.y + frame.height > 1)
        context.addIssue({
            code: "custom",
            path: ["height"],
            message: "The frame must remain inside the normalized page height.",
        });
});
const wholeAssetLocatorSchema = z.object({ type: z.literal("WHOLE_ASSET") }).strict();
const textSpanLocatorSchema = z
    .object({
    type: z.literal("TEXT_SPAN"),
    start: z.number().int().nonnegative(),
    end: z.number().int().positive(),
    quote: z.string().min(1).max(10_000),
})
    .strict()
    .refine(({ start, end }) => end > start, {
    path: ["end"],
    message: "A text-span locator must end after it starts.",
});
const timeRangeLocatorSchema = z
    .object({
    type: z.literal("TIME_RANGE"),
    startMilliseconds: z.number().int().nonnegative(),
    endMilliseconds: z.number().int().positive(),
    transcriptQuote: z.string().min(1).max(10_000).optional(),
})
    .strict()
    .refine(({ startMilliseconds, endMilliseconds }) => endMilliseconds > startMilliseconds, {
    path: ["endMilliseconds"],
    message: "A time-range locator must end after it starts.",
});
const pageRegionLocatorSchema = z
    .object({
    type: z.literal("PAGE_REGION"),
    pageNumber: z.number().int().positive().max(10_000),
    region: normalizedFrameSchema,
})
    .strict();
export const pageUnitSourceLocatorSchema = z.discriminatedUnion("type", [
    wholeAssetLocatorSchema,
    textSpanLocatorSchema,
    timeRangeLocatorSchema,
    pageRegionLocatorSchema,
]);
export const pageUnitSourceReferenceSchema = z
    .object({
    id: opaqueIdSchema,
    order: z.number().int().nonnegative().max(199),
    sourceAssetId: opaqueIdSchema,
    sourceKind: z.enum(["TRANSCRIPT", "AUDIO", "PHOTO", "DOCUMENT", "VIDEO"]),
    evidenceRole: z.enum([
        "FACT",
        "QUOTE",
        "VISUAL_REFERENCE",
        "RECIPE_ORIGIN",
        "LIKENESS_REFERENCE",
    ]),
    locator: pageUnitSourceLocatorSchema,
    label: z.string().trim().min(1).max(200).optional(),
    attribution: z.string().trim().min(1).max(500).optional(),
})
    .strict()
    .superRefine((source, context) => {
    const allowedLocatorTypes = {
        TRANSCRIPT: ["TEXT_SPAN", "TIME_RANGE"],
        AUDIO: ["TIME_RANGE", "WHOLE_ASSET"],
        PHOTO: ["WHOLE_ASSET", "PAGE_REGION"],
        DOCUMENT: ["WHOLE_ASSET", "TEXT_SPAN", "PAGE_REGION"],
        VIDEO: ["TIME_RANGE", "WHOLE_ASSET"],
    };
    if (!allowedLocatorTypes[source.sourceKind].includes(source.locator.type))
        context.addIssue({
            code: "custom",
            path: ["locator", "type"],
            message: `${source.sourceKind} does not support a ${source.locator.type} locator.`,
        });
});
export const pageUnitEvidenceClaimSchema = z
    .object({
    id: opaqueIdSchema,
    order: z.number().int().nonnegative().max(499),
    statement: z.string().trim().min(1).max(10_000),
    status: z.enum(["SUPPORTED", "CHANGED", "UNSUPPORTED", "CREATIVE_DIRECTION"]),
    sourceReferenceIds: z.array(opaqueIdSchema).max(20),
})
    .strict()
    .superRefine((claim, context) => {
    if (new Set(claim.sourceReferenceIds).size !== claim.sourceReferenceIds.length)
        context.addIssue({
            code: "custom",
            path: ["sourceReferenceIds"],
            message: "Evidence source references must be unique.",
        });
    if ((claim.status === "SUPPORTED" || claim.status === "CHANGED") &&
        claim.sourceReferenceIds.length === 0)
        context.addIssue({
            code: "custom",
            path: ["sourceReferenceIds"],
            message: "Supported and changed claims require at least one source reference.",
        });
});
export const pageUnitImagePlacementRoleSchema = z.enum([
    "PRIMARY_ART",
    "PHOTO",
    "DOCUMENT",
    "DECORATIVE",
    "RECIPE_HERO",
    "RECIPE_STEP",
    "COMIC_PANEL_ART",
]);
export const pageUnitImagePlacementSchema = z
    .object({
    id: opaqueIdSchema,
    order: z.number().int().nonnegative().max(31),
    assetId: opaqueIdSchema,
    role: pageUnitImagePlacementRoleSchema,
    slotId: semanticContentIdSchema,
    frame: normalizedFrameSchema,
    fit: z.enum(["CONTAIN", "COVER"]),
    caption: z.string().trim().max(2_000),
    altText: z.string().trim().max(1_000),
    decorative: z.boolean(),
    sourceReferenceIds: z.array(opaqueIdSchema).max(20),
})
    .strict()
    .superRefine((placement, context) => {
    if (new Set(placement.sourceReferenceIds).size !== placement.sourceReferenceIds.length)
        context.addIssue({
            code: "custom",
            path: ["sourceReferenceIds"],
            message: "Placement source references must be unique.",
        });
    if (placement.decorative && placement.altText.length > 0)
        context.addIssue({
            code: "custom",
            path: ["altText"],
            message: "Decorative placements must use empty alt text.",
        });
    if (!placement.decorative && placement.altText.length === 0)
        context.addIssue({
            code: "custom",
            path: ["altText"],
            message: "Meaningful placements require alt text.",
        });
    if ((placement.role === "DECORATIVE") !== placement.decorative)
        context.addIssue({
            code: "custom",
            path: ["decorative"],
            message: "Only DECORATIVE placements may be marked decorative.",
        });
    if (!placement.decorative && placement.sourceReferenceIds.length === 0)
        context.addIssue({
            code: "custom",
            path: ["sourceReferenceIds"],
            message: "Meaningful placements require a provenance source reference.",
        });
});
export const pageUnitReviewStateSchema = z.enum([
    "DRAFT",
    "IN_REVIEW",
    "CHANGES_REQUESTED",
    "APPROVED",
]);
export const pageUnitGateApprovalSchema = z
    .object({
    gateId: creationReviewGateIdSchema,
    reviewerId: opaqueIdSchema,
    reviewerRole: creationReviewerSchema,
    unitRevision: z.number().int().nonnegative(),
    approvedAt: z.string().datetime(),
})
    .strict();
export const pageUnitReviewSchema = z
    .object({
    state: pageUnitReviewStateSchema,
    unitRevision: z.number().int().nonnegative(),
    requiredGateIds: z.array(creationReviewGateIdSchema).min(1).max(16),
    gateApprovals: z.array(pageUnitGateApprovalSchema).max(16),
    findings: z
        .array(z
        .object({
        id: opaqueIdSchema,
        gateId: creationReviewGateIdSchema,
        severity: z.enum(["INFO", "WARNING", "BLOCKING"]),
        status: z.enum(["OPEN", "RESOLVED"]),
        message: z.string().trim().min(1).max(2_000),
    })
        .strict())
        .max(50),
    approvedBy: opaqueIdSchema.optional(),
    approvedAt: z.string().datetime().optional(),
})
    .strict()
    .superRefine((review, context) => {
    if (new Set(review.requiredGateIds).size !== review.requiredGateIds.length)
        context.addIssue({
            code: "custom",
            path: ["requiredGateIds"],
            message: "Required review gates must be unique.",
        });
    if (new Set(review.gateApprovals.map(({ gateId }) => gateId)).size !== review.gateApprovals.length)
        context.addIssue({
            code: "custom",
            path: ["gateApprovals"],
            message: "Each review gate may have only one approval for this unit revision.",
        });
    const required = new Set(review.requiredGateIds);
    review.gateApprovals.forEach((approval, index) => {
        if (!required.has(approval.gateId))
            context.addIssue({
                code: "custom",
                path: ["gateApprovals", index, "gateId"],
                message: "Only required review gates may be marked complete.",
            });
        if (review.findings.some(({ gateId, severity, status }) => gateId === approval.gateId && severity === "BLOCKING" && status === "OPEN"))
            context.addIssue({
                code: "custom",
                path: ["gateApprovals", index, "gateId"],
                message: "A gate with an open blocking finding cannot be approved.",
            });
    });
    if (new Set(review.findings.map(({ id }) => id)).size !== review.findings.length)
        context.addIssue({
            code: "custom",
            path: ["findings"],
            message: "Review finding IDs must be unique.",
        });
    if (review.state === "DRAFT" && review.gateApprovals.length > 0)
        context.addIssue({
            code: "custom",
            path: ["gateApprovals"],
            message: "Draft page units cannot claim completed review gates.",
        });
    if (review.state === "CHANGES_REQUESTED" &&
        !review.findings.some(({ status }) => status === "OPEN"))
        context.addIssue({
            code: "custom",
            path: ["findings"],
            message: "Changes-requested state requires an open review finding.",
        });
    if (review.state === "APPROVED") {
        if (!review.approvedBy || !review.approvedAt)
            context.addIssue({
                code: "custom",
                path: ["approvedBy"],
                message: "Approved page units require approver identity and timestamp.",
            });
        if (review.requiredGateIds.some((gateId) => !review.gateApprovals.some((approval) => approval.gateId === gateId)))
            context.addIssue({
                code: "custom",
                path: ["gateApprovals"],
                message: "Approved page units require every mandatory gate to be complete.",
            });
        if (review.findings.some(({ severity, status }) => severity === "BLOCKING" && status === "OPEN"))
            context.addIssue({
                code: "custom",
                path: ["findings"],
                message: "Approved page units cannot retain open blocking findings.",
            });
        const finalProofApproval = review.gateApprovals.find(({ gateId }) => gateId === "FINAL_PROOF");
        if (finalProofApproval && review.approvedBy !== finalProofApproval.reviewerId)
            context.addIssue({
                code: "custom",
                path: ["approvedBy"],
                message: "Final approval must be attributed to the final-proof reviewer.",
            });
        if (finalProofApproval &&
            review.approvedAt &&
            Date.parse(review.approvedAt) < Date.parse(finalProofApproval.approvedAt))
            context.addIssue({
                code: "custom",
                path: ["approvedAt"],
                message: "Final approval cannot predate the final-proof gate approval.",
            });
    }
    else if (review.approvedBy || review.approvedAt)
        context.addIssue({
            code: "custom",
            path: ["approvedBy"],
            message: "Only approved page units may carry approval attribution.",
        });
});
const pageUnitCommonShape = {
    version: pageUnitContractVersionSchema,
    id: opaqueIdSchema,
    bookId: opaqueIdSchema,
    templateId: currentCatalogTemplateIdSchema,
    order: z.number().int().nonnegative().max(499),
    revision: z.number().int().nonnegative(),
    sourceReferences: z.array(pageUnitSourceReferenceSchema).max(200),
    evidenceClaims: z.array(pageUnitEvidenceClaimSchema).max(500),
    imagePlacements: z.array(pageUnitImagePlacementSchema).max(32),
    review: pageUnitReviewSchema,
};
const frontMatterBlockSchema = z.discriminatedUnion("kind", [
    z
        .object({
        kind: z.literal("TEXT"),
        id: opaqueIdSchema,
        order: z.number().int().nonnegative().max(63),
        style: z.enum(["HEADING", "PARAGRAPH", "DEDICATION", "LEGAL", "CREDIT"]),
        text: z.string().trim().min(1).max(10_000),
    })
        .strict(),
    z
        .object({
        kind: z.literal("IMAGE"),
        id: opaqueIdSchema,
        order: z.number().int().nonnegative().max(63),
        imagePlacementId: opaqueIdSchema,
    })
        .strict(),
    z
        .object({
        kind: z.literal("TOC_ENTRY"),
        id: opaqueIdSchema,
        order: z.number().int().nonnegative().max(63),
        label: z.string().trim().min(1).max(200),
        targetPageUnitId: opaqueIdSchema,
    })
        .strict(),
]);
export const frontMatterPageUnitV1Schema = z
    .object({
    ...pageUnitCommonShape,
    type: z.literal("FRONT_MATTER"),
    bookFamily: pageUnitBookFamilySchema,
    frontMatterKind: z.enum([
        "HALF_TITLE",
        "TITLE_PAGE",
        "COPYRIGHT",
        "DEDICATION",
        "TABLE_OF_CONTENTS",
        "PREFACE",
        "ACKNOWLEDGMENTS",
    ]),
    title: z.string().trim().max(200),
    blocks: z.array(frontMatterBlockSchema).min(1).max(64),
})
    .strict();
const narrativeBlockSchema = z.discriminatedUnion("kind", [
    z
        .object({
        kind: z.literal("HEADING"),
        id: opaqueIdSchema,
        order: z.number().int().nonnegative().max(199),
        level: z.number().int().min(2).max(4),
        text: z.string().trim().min(1).max(300),
    })
        .strict(),
    z
        .object({
        kind: z.literal("PARAGRAPH"),
        id: opaqueIdSchema,
        order: z.number().int().nonnegative().max(199),
        text: z.string().trim().min(1).max(10_000),
        evidenceClaimIds: z.array(opaqueIdSchema).max(50),
    })
        .strict(),
    z
        .object({
        kind: z.literal("QUOTE"),
        id: opaqueIdSchema,
        order: z.number().int().nonnegative().max(199),
        text: z.string().trim().min(1).max(2_000),
        attribution: z.string().trim().min(1).max(300),
        evidenceClaimId: opaqueIdSchema,
    })
        .strict(),
    z
        .object({
        kind: z.literal("IMAGE"),
        id: opaqueIdSchema,
        order: z.number().int().nonnegative().max(199),
        imagePlacementId: opaqueIdSchema,
    })
        .strict(),
]);
export const narrativeChapterPageUnitV1Schema = z
    .object({
    ...pageUnitCommonShape,
    type: z.literal("NARRATIVE_CHAPTER"),
    bookFamily: z.literal("NARRATIVE"),
    chapterNumber: z.number().int().positive().max(250),
    title: z.string().trim().min(1).max(200),
    summary: z.string().trim().max(2_000),
    blocks: z.array(narrativeBlockSchema).min(1).max(200),
})
    .strict();
const pictureBookTextMomentSchema = z
    .object({
    id: opaqueIdSchema,
    order: z.number().int().nonnegative().max(7),
    kind: z.enum(["NARRATION", "REFRAIN", "READER_PROMPT"]),
    text: z.string().trim().min(1).max(1_000),
    delivery: z.string().trim().max(300),
    evidenceClaimIds: z.array(opaqueIdSchema).max(20),
})
    .strict();
const physicalSpreadPagesSchema = z
    .tuple([z.number().int().positive().max(1_000), z.number().int().positive().max(1_000)])
    .refine(([left, right]) => right === left + 1, {
    message: "A picture-book spread must contain adjacent physical pages.",
});
export const pictureBookSpreadPageUnitV1Schema = z
    .object({
    ...pageUnitCommonShape,
    type: z.literal("PICTURE_BOOK_SPREAD"),
    bookFamily: z.literal("PICTURE_BOOK"),
    spreadNumber: z.number().int().positive().max(100),
    physicalPageNumbers: physicalSpreadPagesSchema,
    narrativePurpose: z.string().trim().min(1).max(1_000),
    visualDirection: z.string().trim().min(1).max(5_000),
    artPlacementId: opaqueIdSchema,
    textMoments: z.array(pictureBookTextMomentSchema).min(1).max(8),
    continuityAnchorIds: z.array(opaqueIdSchema).max(20),
})
    .strict();
const recipeIngredientSchema = z
    .object({
    id: z.string().trim().min(1).max(80),
    order: z.number().int().nonnegative().max(99),
    quantity: z.string().trim().min(1).max(40),
    unit: z.string().trim().max(40),
    ingredient: z.string().trim().min(1).max(240),
    preparation: z.string().trim().max(240),
    approved: z.boolean(),
    evidenceClaimIds: z.array(opaqueIdSchema).max(20),
})
    .strict();
const recipeIngredientGroupSchema = z
    .object({
    id: z.string().trim().min(1).max(80),
    order: z.number().int().nonnegative().max(19),
    title: z.string().trim().max(120),
    ingredients: z.array(recipeIngredientSchema).min(1).max(100),
})
    .strict();
const recipeInstructionSchema = z
    .object({
    id: z.string().trim().min(1).max(80),
    order: z.number().int().nonnegative().max(99),
    text: z.string().trim().min(1).max(2_000),
    approved: z.boolean(),
    imagePlacementId: opaqueIdSchema.optional(),
    evidenceClaimIds: z.array(opaqueIdSchema).max(20),
})
    .strict();
export const recipePageUnitV1Schema = z
    .object({
    ...pageUnitCommonShape,
    type: z.literal("RECIPE_PAGE"),
    bookFamily: z.literal("RECIPE"),
    recipeId: semanticContentIdSchema,
    recipePageNumber: z.number().int().positive().max(20),
    recipePageRole: z.enum(["RECIPE_OPENER", "RECIPE_CONTINUATION"]),
    title: z.string().trim().min(1).max(200),
    familyStory: z.string().trim().max(20_000),
    yield: z.string().trim().max(100),
    preparationMinutes: z.number().int().nonnegative().max(10_000).nullable(),
    cookingMinutes: z.number().int().nonnegative().max(10_000).nullable(),
    ingredientGroups: z.array(recipeIngredientGroupSchema).max(20),
    instructions: z.array(recipeInstructionSchema).max(100),
    notes: z.string().trim().max(10_000),
    variations: z.string().trim().max(10_000),
    attribution: z.string().trim().max(500),
    dietaryNotes: z.array(z.string().trim().min(1).max(100)).max(30),
    safetyNotes: z.array(z.string().trim().min(1).max(500)).max(20),
    needsStructureReview: z.boolean(),
    heroImagePlacementId: opaqueIdSchema.optional(),
})
    .strict();
export const comicBalloonV1Schema = z
    .object({
    id: opaqueIdSchema,
    order: z.number().int().nonnegative().max(7),
    kind: z.enum(["SPEECH", "THOUGHT", "SOUND_EFFECT"]),
    speaker: z.string().trim().min(1).max(100).optional(),
    text: z.string().trim().min(1).max(180),
})
    .strict()
    .superRefine((balloon, context) => {
    if (balloon.kind !== "SOUND_EFFECT" && !balloon.speaker)
        context.addIssue({
            code: "custom",
            path: ["speaker"],
            message: "Speech and thought balloons require a speaker.",
        });
    if (balloon.kind === "SOUND_EFFECT" && balloon.speaker)
        context.addIssue({
            code: "custom",
            path: ["speaker"],
            message: "Sound-effect lettering cannot claim a speaker.",
        });
});
export const comicPanelV1Schema = z
    .object({
    type: z.literal("COMIC_PANEL"),
    id: opaqueIdSchema,
    order: z.number().int().nonnegative().max(7),
    frame: normalizedFrameSchema,
    visualDirection: z.string().trim().min(20).max(5_000),
    imagePlacementId: opaqueIdSchema,
    caption: z.string().trim().max(180),
    balloons: z.array(comicBalloonV1Schema).max(8),
    evidenceClaimIds: z.array(opaqueIdSchema).max(50),
})
    .strict();
export const comicPageUnitV1Schema = z
    .object({
    ...pageUnitCommonShape,
    type: z.literal("COMIC_PAGE"),
    bookFamily: z.literal("COMIC_CORE"),
    issueId: semanticContentIdSchema,
    pageNumber: z.number().int().positive().max(24),
    beat: z.string().trim().min(1).max(2_000),
    pageTurn: z.string().trim().max(1_000),
    panels: z.array(comicPanelV1Schema).min(1).max(8),
})
    .strict();
function validateOrderedItems(items, context, path) {
    if (new Set(items.map(({ id }) => id)).size !== items.length)
        context.addIssue({
            code: "custom",
            path,
            message: "Stable item IDs must be unique within their ordered collection.",
        });
    items.forEach((item, index) => {
        if (item.order !== index)
            context.addIssue({
                code: "custom",
                path: [...path, index, "order"],
                message: "Ordered items must use contiguous zero-based order in array order.",
            });
    });
}
function validateUniqueStrings(values, context, path) {
    if (new Set(values).size !== values.length)
        context.addIssue({ code: "custom", path, message: "References must be unique." });
}
function validateReferences(values, available, context, path, label) {
    validateUniqueStrings(values, context, path);
    values.forEach((value, index) => {
        if (!available.has(value))
            context.addIssue({
                code: "custom",
                path: [...path, index],
                message: `${label} must reference an item in the same page unit.`,
            });
    });
}
function normalizeEvidenceText(value) {
    return value.toLocaleLowerCase("en-US").replaceAll(/\s+/gu, " ").trim();
}
function sourceReferenceQuote(source) {
    if (source.locator.type === "TEXT_SPAN")
        return source.locator.quote;
    if (source.locator.type === "TIME_RANGE")
        return source.locator.transcriptQuote;
    return undefined;
}
function exactQuoteIsSupported(quote, attribution, claim, sources) {
    const normalizedQuote = normalizeEvidenceText(quote);
    if (claim.status !== "SUPPORTED" ||
        !normalizeEvidenceText(claim.statement).includes(normalizedQuote))
        return false;
    return claim.sourceReferenceIds.some((sourceReferenceId) => {
        const source = sources.get(sourceReferenceId);
        if (!source || source.evidenceRole !== "QUOTE")
            return false;
        const sourceQuote = sourceReferenceQuote(source);
        return Boolean(sourceQuote &&
            normalizeEvidenceText(sourceQuote).includes(normalizedQuote) &&
            source.attribution &&
            normalizeEvidenceText(source.attribution) === normalizeEvidenceText(attribution));
    });
}
const rawPageUnitV1Schema = z.discriminatedUnion("type", [
    frontMatterPageUnitV1Schema,
    narrativeChapterPageUnitV1Schema,
    pictureBookSpreadPageUnitV1Schema,
    recipePageUnitV1Schema,
    comicPageUnitV1Schema,
]);
export const pageUnitV1Schema = rawPageUnitV1Schema.superRefine((unit, context) => {
    validateOrderedItems(unit.sourceReferences, context, ["sourceReferences"]);
    validateOrderedItems(unit.evidenceClaims, context, ["evidenceClaims"]);
    validateOrderedItems(unit.imagePlacements, context, ["imagePlacements"]);
    validateUniqueStrings(unit.imagePlacements.map(({ slotId }) => slotId), context, ["imagePlacements"]);
    const sourceIds = new Set(unit.sourceReferences.map(({ id }) => id));
    const sources = new Map(unit.sourceReferences.map((source) => [source.id, source]));
    const claimIds = new Set(unit.evidenceClaims.map(({ id }) => id));
    const claims = new Map(unit.evidenceClaims.map((claim) => [claim.id, claim]));
    const placements = new Map(unit.imagePlacements.map((placement) => [placement.id, placement]));
    unit.evidenceClaims.forEach((claim, index) => validateReferences(claim.sourceReferenceIds, sourceIds, context, ["evidenceClaims", index, "sourceReferenceIds"], "Evidence"));
    unit.imagePlacements.forEach((placement, index) => validateReferences(placement.sourceReferenceIds, sourceIds, context, ["imagePlacements", index, "sourceReferenceIds"], "Image placement"));
    unit.imagePlacements.forEach((placement, index) => {
        if (!placement.decorative &&
            !placement.sourceReferenceIds.some((sourceReferenceId) => {
                const source = sources.get(sourceReferenceId);
                return (source?.evidenceRole === "VISUAL_REFERENCE" ||
                    source?.evidenceRole === "RECIPE_ORIGIN" ||
                    source?.evidenceRole === "LIKENESS_REFERENCE");
            }))
            context.addIssue({
                code: "custom",
                path: ["imagePlacements", index, "sourceReferenceIds"],
                message: "Meaningful placements require a visual, recipe-origin, or likeness source.",
            });
    });
    const profile = bookCreationProfileForTemplate(unit.templateId);
    if (!profile || profile.registration !== "CURRENT_CATALOG")
        context.addIssue({
            code: "custom",
            path: ["templateId"],
            message: "Page units require a registered current-catalog creation profile.",
        });
    else {
        if (profile.productionFamily !== unit.bookFamily)
            context.addIssue({
                code: "custom",
                path: ["bookFamily"],
                message: "The page-unit family must match its registered template profile.",
            });
        const requiredGates = profile.reviewGates
            .filter(({ blocking }) => blocking)
            .map(({ id }) => id);
        if (requiredGates.length !== unit.review.requiredGateIds.length ||
            requiredGates.some((gateId, index) => unit.review.requiredGateIds[index] !== gateId))
            context.addIssue({
                code: "custom",
                path: ["review", "requiredGateIds"],
                message: "Required gates must exactly match the registered template review policy.",
            });
        const profileGates = new Map(profile.reviewGates.map((gate) => [gate.id, gate]));
        unit.review.gateApprovals.forEach((approval, index) => {
            const gate = profileGates.get(approval.gateId);
            if (!gate)
                context.addIssue({
                    code: "custom",
                    path: ["review", "gateApprovals", index, "gateId"],
                    message: "Gate approvals must belong to the registered template review policy.",
                });
            else if (gate.reviewer !== approval.reviewerRole)
                context.addIssue({
                    code: "custom",
                    path: ["review", "gateApprovals", index, "reviewerRole"],
                    message: "The gate approval must use the reviewer role required by the template.",
                });
        });
        unit.review.findings.forEach((finding, index) => {
            if (!profileGates.has(finding.gateId))
                context.addIssue({
                    code: "custom",
                    path: ["review", "findings", index, "gateId"],
                    message: "Review findings must belong to the registered template review policy.",
                });
        });
        if (profile.readiness === "CONTRACT_ONLY" && unit.review.state === "APPROVED")
            context.addIssue({
                code: "custom",
                path: ["review", "state"],
                message: "Contract-only editions cannot claim production approval.",
            });
    }
    if (unit.review.unitRevision !== unit.revision)
        context.addIssue({
            code: "custom",
            path: ["review", "unitRevision"],
            message: "Review state must be bound to the current immutable unit revision.",
        });
    unit.review.gateApprovals.forEach((approval, index) => {
        if (approval.unitRevision !== unit.revision)
            context.addIssue({
                code: "custom",
                path: ["review", "gateApprovals", index, "unitRevision"],
                message: "Gate approvals must be bound to the current immutable unit revision.",
            });
    });
    if (unit.review.state === "APPROVED")
        unit.evidenceClaims.forEach((claim, index) => {
            if (claim.status === "CHANGED" || claim.status === "UNSUPPORTED")
                context.addIssue({
                    code: "custom",
                    path: ["evidenceClaims", index, "status"],
                    message: "Approved units cannot retain changed or unsupported evidence claims.",
                });
        });
    if (unit.type === "FRONT_MATTER") {
        validateOrderedItems(unit.blocks, context, ["blocks"]);
        unit.blocks.forEach((block, index) => {
            if (block.kind === "IMAGE" && !placements.has(block.imagePlacementId))
                context.addIssue({
                    code: "custom",
                    path: ["blocks", index, "imagePlacementId"],
                    message: "Front-matter image blocks require a placement in the same unit.",
                });
            if (block.kind === "TOC_ENTRY" && unit.frontMatterKind !== "TABLE_OF_CONTENTS")
                context.addIssue({
                    code: "custom",
                    path: ["blocks", index, "kind"],
                    message: "TOC entries are allowed only in table-of-contents front matter.",
                });
        });
        if (unit.frontMatterKind === "TABLE_OF_CONTENTS" &&
            !unit.blocks.some(({ kind }) => kind === "TOC_ENTRY"))
            context.addIssue({
                code: "custom",
                path: ["blocks"],
                message: "A table-of-contents unit requires at least one TOC entry.",
            });
    }
    if (unit.type === "NARRATIVE_CHAPTER") {
        validateOrderedItems(unit.blocks, context, ["blocks"]);
        const allowedRoles = new Set(["PHOTO", "DOCUMENT", "DECORATIVE"]);
        unit.imagePlacements.forEach((placement, index) => {
            if (!allowedRoles.has(placement.role))
                context.addIssue({
                    code: "custom",
                    path: ["imagePlacements", index, "role"],
                    message: "Narrative chapters accept only photo, document, or decorative placements.",
                });
        });
        unit.blocks.forEach((block, index) => {
            if (block.kind === "PARAGRAPH") {
                validateReferences(block.evidenceClaimIds, claimIds, context, ["blocks", index, "evidenceClaimIds"], "Narrative paragraph evidence");
                if (unit.review.state === "APPROVED" &&
                    block.evidenceClaimIds.some((evidenceClaimId) => claims.get(evidenceClaimId)?.status === "CREATIVE_DIRECTION"))
                    context.addIssue({
                        code: "custom",
                        path: ["blocks", index, "evidenceClaimIds"],
                        message: "Creative direction cannot satisfy narrative factual evidence.",
                    });
            }
            if (block.kind === "QUOTE") {
                const claim = claims.get(block.evidenceClaimId);
                if (!claim)
                    context.addIssue({
                        code: "custom",
                        path: ["blocks", index, "evidenceClaimId"],
                        message: "Narrative quotes require an evidence claim in the same unit.",
                    });
                else if (unit.review.state === "APPROVED" &&
                    !exactQuoteIsSupported(block.text, block.attribution, claim, sources))
                    context.addIssue({
                        code: "custom",
                        path: ["blocks", index, "evidenceClaimId"],
                        message: "Approved quotations require exact, quote-role source support.",
                    });
            }
            if (block.kind === "IMAGE" && !placements.has(block.imagePlacementId))
                context.addIssue({
                    code: "custom",
                    path: ["blocks", index, "imagePlacementId"],
                    message: "Narrative image blocks require a placement in the same unit.",
                });
        });
    }
    if (unit.type === "PICTURE_BOOK_SPREAD") {
        validateOrderedItems(unit.textMoments, context, ["textMoments"]);
        validateUniqueStrings(unit.continuityAnchorIds, context, ["continuityAnchorIds"]);
        const art = placements.get(unit.artPlacementId);
        if (!art)
            context.addIssue({
                code: "custom",
                path: ["artPlacementId"],
                message: "A picture-book spread requires its art placement in the same unit.",
            });
        else if (art.role !== "PRIMARY_ART")
            context.addIssue({
                code: "custom",
                path: ["artPlacementId"],
                message: "A picture-book spread's art placement must use PRIMARY_ART.",
            });
        unit.imagePlacements.forEach((placement, index) => {
            if (!["PRIMARY_ART", "DECORATIVE"].includes(placement.role))
                context.addIssue({
                    code: "custom",
                    path: ["imagePlacements", index, "role"],
                    message: "Picture-book spreads accept only primary-art or decorative placements.",
                });
        });
        unit.textMoments.forEach((moment, index) => validateReferences(moment.evidenceClaimIds, claimIds, context, ["textMoments", index, "evidenceClaimIds"], "Picture-book text evidence"));
    }
    if (unit.type === "RECIPE_PAGE") {
        validateOrderedItems(unit.ingredientGroups, context, ["ingredientGroups"]);
        unit.ingredientGroups.forEach((group, groupIndex) => {
            validateOrderedItems(group.ingredients, context, [
                "ingredientGroups",
                groupIndex,
                "ingredients",
            ]);
            group.ingredients.forEach((ingredient, ingredientIndex) => {
                validateReferences(ingredient.evidenceClaimIds, claimIds, context, ["ingredientGroups", groupIndex, "ingredients", ingredientIndex, "evidenceClaimIds"], "Recipe ingredient evidence");
                if (unit.review.state === "APPROVED") {
                    if (!ingredient.approved)
                        context.addIssue({
                            code: "custom",
                            path: ["ingredientGroups", groupIndex, "ingredients", ingredientIndex, "approved"],
                            message: "Approved recipe pages require every ingredient to be approved.",
                        });
                    if (ingredient.evidenceClaimIds.length === 0 ||
                        ingredient.evidenceClaimIds.some((claimId) => claims.get(claimId)?.status !== "SUPPORTED"))
                        context.addIssue({
                            code: "custom",
                            path: [
                                "ingredientGroups",
                                groupIndex,
                                "ingredients",
                                ingredientIndex,
                                "evidenceClaimIds",
                            ],
                            message: "Approved recipe ingredients require supported source evidence.",
                        });
                }
            });
        });
        validateOrderedItems(unit.instructions, context, ["instructions"]);
        unit.instructions.forEach((instruction, index) => {
            validateReferences(instruction.evidenceClaimIds, claimIds, context, ["instructions", index, "evidenceClaimIds"], "Recipe instruction evidence");
            if (unit.review.state === "APPROVED") {
                if (!instruction.approved)
                    context.addIssue({
                        code: "custom",
                        path: ["instructions", index, "approved"],
                        message: "Approved recipe pages require every instruction to be approved.",
                    });
                if (instruction.evidenceClaimIds.length === 0 ||
                    instruction.evidenceClaimIds.some((claimId) => claims.get(claimId)?.status !== "SUPPORTED"))
                    context.addIssue({
                        code: "custom",
                        path: ["instructions", index, "evidenceClaimIds"],
                        message: "Approved recipe instructions require supported source evidence.",
                    });
            }
            if (instruction.imagePlacementId) {
                const placement = placements.get(instruction.imagePlacementId);
                if (!placement || placement.role !== "RECIPE_STEP")
                    context.addIssue({
                        code: "custom",
                        path: ["instructions", index, "imagePlacementId"],
                        message: "Recipe instruction images require a RECIPE_STEP placement in the same unit.",
                    });
            }
        });
        if (unit.heroImagePlacementId) {
            const hero = placements.get(unit.heroImagePlacementId);
            if (!hero || hero.role !== "RECIPE_HERO")
                context.addIssue({
                    code: "custom",
                    path: ["heroImagePlacementId"],
                    message: "Recipe hero images require a RECIPE_HERO placement in the same unit.",
                });
            if (unit.recipePageRole !== "RECIPE_OPENER")
                context.addIssue({
                    code: "custom",
                    path: ["heroImagePlacementId"],
                    message: "Only a recipe opener may carry the hero image.",
                });
        }
        unit.imagePlacements.forEach((placement, index) => {
            if (!["RECIPE_HERO", "RECIPE_STEP", "PHOTO", "DOCUMENT", "DECORATIVE"].includes(placement.role))
                context.addIssue({
                    code: "custom",
                    path: ["imagePlacements", index, "role"],
                    message: "Recipe pages contain only recipe, photo, or decorative placements.",
                });
        });
        if (unit.review.state === "APPROVED" && unit.needsStructureReview)
            context.addIssue({
                code: "custom",
                path: ["needsStructureReview"],
                message: "A recipe needing structure review cannot be approved.",
            });
    }
    if (unit.type === "COMIC_PAGE") {
        validateOrderedItems(unit.panels, context, ["panels"]);
        let pageTextCharacters = 0;
        unit.panels.forEach((panel, panelIndex) => {
            validateOrderedItems(panel.balloons, context, ["panels", panelIndex, "balloons"]);
            validateReferences(panel.evidenceClaimIds, claimIds, context, ["panels", panelIndex, "evidenceClaimIds"], "Comic-panel evidence");
            const panelArt = placements.get(panel.imagePlacementId);
            if (!panelArt || panelArt.role !== "COMIC_PANEL_ART")
                context.addIssue({
                    code: "custom",
                    path: ["panels", panelIndex, "imagePlacementId"],
                    message: "Every comic panel requires a COMIC_PANEL_ART placement in the same unit.",
                });
            const panelTextCharacters = panel.caption.length +
                panel.balloons.reduce((sum, balloon) => sum + balloon.text.length, 0);
            pageTextCharacters += panelTextCharacters;
            if (panelTextCharacters > 420)
                context.addIssue({
                    code: "custom",
                    path: ["panels", panelIndex],
                    message: "Comic panel text cannot exceed 420 characters.",
                });
        });
        if (pageTextCharacters > 1_200)
            context.addIssue({
                code: "custom",
                path: ["panels"],
                message: "Comic page text cannot exceed 1,200 characters.",
            });
        unit.imagePlacements.forEach((placement, index) => {
            if (!["COMIC_PANEL_ART", "DECORATIVE"].includes(placement.role))
                context.addIssue({
                    code: "custom",
                    path: ["imagePlacements", index, "role"],
                    message: "Comic pages accept only panel-art or decorative placements.",
                });
        });
    }
});
export const pageUnitContinuityAnchorSchema = z
    .object({
    id: opaqueIdSchema,
    kind: z.enum(["CHARACTER", "SETTING", "PROP", "PALETTE", "STYLE"]),
    label: z.string().trim().min(1).max(160),
    description: z.string().trim().min(1).max(2_000),
})
    .strict();
function validateContiguousFamilyNumbers(entries, field, context) {
    entries.forEach((entry, position) => {
        if (entry.value !== position + 1)
            context.addIssue({
                code: "custom",
                path: ["units", entry.index, field],
                message: `${field} must be unique, contiguous, and ordered from 1.`,
            });
    });
}
export const bookPageUnitCollectionV1Schema = z
    .object({
    version: pageUnitContractVersionSchema,
    bookId: opaqueIdSchema,
    templateId: currentCatalogTemplateIdSchema,
    bookFamily: pageUnitBookFamilySchema,
    revision: z.number().int().nonnegative(),
    continuityAnchors: z.array(pageUnitContinuityAnchorSchema).max(100),
    units: z.array(pageUnitV1Schema).min(1).max(500),
})
    .strict()
    .superRefine((book, context) => {
    validateOrderedItems(book.units, context, ["units"]);
    const profile = bookCreationProfileForTemplate(book.templateId);
    if (!profile || profile.registration !== "CURRENT_CATALOG")
        context.addIssue({
            code: "custom",
            path: ["templateId"],
            message: "Page-unit collections require a registered current-catalog template.",
        });
    else if (profile.productionFamily !== book.bookFamily)
        context.addIssue({
            code: "custom",
            path: ["bookFamily"],
            message: "The collection family must match its registered template profile.",
        });
    const unitIds = new Set(book.units.map(({ id }) => id));
    validateUniqueStrings(book.continuityAnchors.map(({ id }) => id), context, ["continuityAnchors"]);
    const continuityAnchorIds = new Set(book.continuityAnchors.map(({ id }) => id));
    book.units.forEach((unit, index) => {
        if (unit.bookId !== book.bookId)
            context.addIssue({
                code: "custom",
                path: ["units", index, "bookId"],
                message: "Every page unit must belong to the enclosing book.",
            });
        if (unit.bookFamily !== book.bookFamily)
            context.addIssue({
                code: "custom",
                path: ["units", index, "bookFamily"],
                message: "Every page unit must use the enclosing book's production family.",
            });
        if (unit.templateId !== book.templateId)
            context.addIssue({
                code: "custom",
                path: ["units", index, "templateId"],
                message: "Every page unit must use the enclosing book's registered template.",
            });
        if (unit.type === "FRONT_MATTER")
            unit.blocks.forEach((block, blockIndex) => {
                if (block.kind === "TOC_ENTRY" && !unitIds.has(block.targetPageUnitId))
                    context.addIssue({
                        code: "custom",
                        path: ["units", index, "blocks", blockIndex, "targetPageUnitId"],
                        message: "TOC entries must target a page unit in the same book.",
                    });
            });
        if (unit.type === "PICTURE_BOOK_SPREAD")
            validateReferences(unit.continuityAnchorIds, continuityAnchorIds, context, ["units", index, "continuityAnchorIds"], "Picture-book continuity anchors");
    });
    const narrativeChapters = book.units.flatMap((unit, index) => unit.type === "NARRATIVE_CHAPTER" ? [{ index, value: unit.chapterNumber }] : []);
    validateContiguousFamilyNumbers(narrativeChapters, "chapterNumber", context);
    const pictureSpreads = book.units.flatMap((unit, index) => unit.type === "PICTURE_BOOK_SPREAD" ? [{ index, unit, value: unit.spreadNumber }] : []);
    validateContiguousFamilyNumbers(pictureSpreads, "spreadNumber", context);
    const physicalPages = new Map();
    let previousPhysicalRightPage = 0;
    pictureSpreads.forEach(({ unit, index }) => {
        if (unit.physicalPageNumbers[0] <= previousPhysicalRightPage)
            context.addIssue({
                code: "custom",
                path: ["units", index, "physicalPageNumbers"],
                message: "Picture-book physical page ranges must increase in spread order.",
            });
        previousPhysicalRightPage = unit.physicalPageNumbers[1];
        unit.physicalPageNumbers.forEach((pageNumber, pageIndex) => {
            const priorIndex = physicalPages.get(pageNumber);
            if (priorIndex !== undefined)
                context.addIssue({
                    code: "custom",
                    path: ["units", index, "physicalPageNumbers", pageIndex],
                    message: "Picture-book physical page ranges cannot overlap.",
                });
            else
                physicalPages.set(pageNumber, index);
        });
    });
    const comicIssues = new Map();
    book.units.forEach((unit, index) => {
        if (unit.type !== "COMIC_PAGE")
            return;
        const entries = comicIssues.get(unit.issueId) ?? [];
        entries.push({ index, value: unit.pageNumber });
        comicIssues.set(unit.issueId, entries);
    });
    comicIssues.forEach((entries) => validateContiguousFamilyNumbers(entries, "pageNumber", context));
    const recipeGroups = new Map();
    book.units.forEach((unit, index) => {
        if (unit.type !== "RECIPE_PAGE")
            return;
        const entries = recipeGroups.get(unit.recipeId) ?? [];
        entries.push({ index, unit });
        recipeGroups.set(unit.recipeId, entries);
    });
    recipeGroups.forEach((entries) => {
        validateContiguousFamilyNumbers(entries.map(({ index, unit }) => ({ index, value: unit.recipePageNumber })), "recipePageNumber", context);
        const openerCount = entries.filter(({ unit }) => unit.recipePageRole === "RECIPE_OPENER").length;
        if (openerCount !== 1)
            context.addIssue({
                code: "custom",
                path: ["units", entries[0].index, "recipePageRole"],
                message: "Each recipe requires exactly one opener.",
            });
        entries.forEach(({ unit, index }, position) => {
            const expectedRole = position === 0 ? "RECIPE_OPENER" : "RECIPE_CONTINUATION";
            if (unit.recipePageRole !== expectedRole)
                context.addIssue({
                    code: "custom",
                    path: ["units", index, "recipePageRole"],
                    message: "Recipe page 1 is the opener and every later page is a continuation.",
                });
        });
        const ingredientCount = entries.reduce((count, { unit }) => count + unit.ingredientGroups.reduce((sum, group) => sum + group.ingredients.length, 0), 0);
        const instructionCount = entries.reduce((count, { unit }) => count + unit.instructions.length, 0);
        if (ingredientCount === 0)
            context.addIssue({
                code: "custom",
                path: ["units", entries[0].index, "ingredientGroups"],
                message: "A complete recipe requires at least one ingredient.",
            });
        if (instructionCount === 0)
            context.addIssue({
                code: "custom",
                path: ["units", entries[0].index, "instructions"],
                message: "A complete recipe requires at least one instruction.",
            });
    });
});
//# sourceMappingURL=page-units.js.map